import React, { useEffect, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'
import Modal, { ModalProps } from 'components/modal'
import Button from 'components/button'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import { Plus } from 'components/icons'
import { HUB } from 'routes'
import { NEW_DOCUMENT_STATE } from 'components/document/helpers/newDocument'
import useFocusWhenOpen from 'hooks/useFocusWhenOpen'
import { ISite } from 'types'
import Select from 'components/select'
import Input from 'components/input'

interface CreateHubModalProps extends ModalProps {
  onCreate?: () => void
  /** Pre-selects a site, e.g. when opened from that site's section in the sidebar. */
  defaultSiteId?: string
}

const CreateHubModal: React.FC<CreateHubModalProps> = ({ id, open, setOpen, onCreate, defaultSiteId }) => {
  const [newProject, setNewProject] = useState<any>({
    name: `New ${capitaliseFirstLetter(PROJECT)}`,
    template: 'None',
    loading: false
  })

  const { displayErrorMessage } = useApplicationStore()
  const navigate = useNavigate()

  // Only the sites the user may create a hub in; the rest are left out of the picker.
  const [sites, setSites] = useState<ISite[]>([])
  // Until the user's sites are known we can't tell whether a site is required, so don't create yet
  const [sitesLoaded, setSitesLoaded] = useState(false)
  const [selectedSite, setSelectedSite] = useState<string | null>(defaultSiteId ?? null)
  const nameInput = useFocusWhenOpen(open)
  const canCreate =
    sitesLoaded && newProject.name.trim() !== '' && !newProject.loading && !(sites.length > 0 && !selectedSite)

  useEffect(() => {
    api
      .getUserSites()
      .then((response) => {
        setSites(response.data.filter((site) => site.canCreateHub !== false))
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
      .finally(() => {
        setSitesLoaded(true)
      })
  }, [])

  const handleClick = async () => {
    setNewProject({ ...newProject, loading: true })
    api
      .createProject(newProject.name, selectedSite || undefined)
      .then((response) => {
        setOpen(false)
        setNewProject({
          name: `New ${capitaliseFirstLetter(PROJECT)}`,
          loading: false
        })
        onCreate?.()
        const projectId = response.data.publicId
        // The hub redirects to its new homepage document, which opens ready to edit
        navigate(generatePath(HUB, { projectId: projectId }), { state: NEW_DOCUMENT_STATE })
      })
      .catch((error) => {
        displayErrorMessage(error)
        setNewProject({ ...newProject, loading: false })
      })
  }

  return (
    <Modal id={id} open={open} setOpen={setOpen} title={`Create New ${capitaliseFirstLetter(PROJECT)}`}>
      <div className="mb-8">
        <label htmlFor="hub-name-input" className="block text-sm text-muted mb-[5px]">
          {capitaliseFirstLetter(PROJECT)} Name
        </label>
        <Input
          ref={nameInput}
          required
          data-cy="project-name-input"
          data-tour="hub-name-input"
          placeholder={`The name of your new ${PROJECT}`}
          defaultValue={newProject.name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setNewProject({ ...newProject, name: event.target.value })
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canCreate) handleClick()
          }}
        />
      </div>

      {sites && sites.length > 0 && (
        <div className="mb-8">
          <label htmlFor="hub-site-select" className="block text-sm text-muted mb-[5px]">
            Associate with Organisation Site (Required)
          </label>

          <div className="w-full">
            <Select
              options={sites.map((site) => ({ label: site.name, value: site.publicId }))}
              optionsSelected={[selectedSite || '']}
              onOptionClick={(option) => setSelectedSite(option)}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button internalType="danger" className="mr-2 w-[180px]" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button
          data-cy="create-hub-button"
          data-tour="create-hub-submit"
          internalType="accept"
          onClick={() => handleClick()}
          isLoading={newProject.loading}
          disabled={!canCreate}
          className="w-[180px]"
        >
          <Plus className="mr-2.5" />
          Create {capitaliseFirstLetter(PROJECT)}
        </Button>
      </div>
    </Modal>
  )
}

export default CreateHubModal
